/*
 * Theme bootstrap shared by all three portals.
 *
 * `bootstrapTheme` runs once at app start (before React renders). It
 * picks a theme using this precedence:
 *   1. an explicit user override at `localStorage['mgp.theme']`
 *   2. the OS `prefers-color-scheme` media query
 *   3. fall back to light
 *
 * It also wires a media-query listener so flipping the OS theme
 * follows through unless the user has set an override.
 *
 * The active class lives on <html> (Tailwind's `darkMode: 'class'`
 * looks there). The setter is exported so a future Phase 2 toggle
 * component can call it without re-implementing the storage key.
 */

const STORAGE_KEY = 'mgp.theme';
const CLASS_NAME = 'dark';

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';

function readStoredPreference(): ThemePreference {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // localStorage disabled / private browsing — fall through to system.
  }
  return 'system';
}

function systemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add(CLASS_NAME);
  } else {
    root.classList.remove(CLASS_NAME);
  }
  root.dataset.theme = theme;
}

export function resolveTheme(pref: ThemePreference): Theme {
  return pref === 'system' ? systemTheme() : pref;
}

export function getThemePreference(): ThemePreference {
  return readStoredPreference();
}

export function setThemePreference(pref: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // ignored
  }
  applyTheme(resolveTheme(pref));
}

/**
 * Apply the user's theme on first paint and keep it in sync with the
 * OS preference when no explicit override is set.
 *
 * Returns a teardown function that detaches the media listener — only
 * useful in tests; production callers can ignore it.
 */
export function bootstrapTheme(): () => void {
  if (typeof document === 'undefined') return () => {};
  const pref = readStoredPreference();
  applyTheme(resolveTheme(pref));

  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (readStoredPreference() === 'system') {
      applyTheme(mql.matches ? 'dark' : 'light');
    }
  };
  mql.addEventListener?.('change', onChange);
  return () => mql.removeEventListener?.('change', onChange);
}
